import { CHESS_SIDE, CHESS_START_POSITION } from "../constants";
import { checkGameResult, generateResultString } from "../utils/result-helper";
import { chessgroundToChessBase, mcqToChessBase, orientToChessBase, } from "./Annotation";
export class PgnMove {
    id;
    number;
    color;
    san;
    from;
    to;
    promotion;
    fen;
    pgn;
    variationDepth = 0;
    parentMove;
    childMoves;
    previousMoveInline;
    nextMove;
    comment;
    nag;
    shapes;
    mcq;
    puzzleAnnotation; // Puzzle settings — set on the anchor move before {[#]}
    orient; // Board orientation override from [%orient w|b]
    /** Back-link to preMove, set only on root moves (no previousMoveInline, no parentMove) */
    _preMove = null;
    _userScore = null;
    _isRevealed = false;
    _gameResult;
    /**
     * Creates a "pre-move" - a virtual move that represents the state before the first move.
     * This is used to store pre-game comments, shapes, and MCQ annotations.
     *
     * @param fen - The initial FEN position
     * @returns A PgnMove instance without an ID (representing pre-game state)
     */
    static createPreMove(fen = CHESS_START_POSITION) {
        const preMove = Object.create(PgnMove.prototype);
        // Set minimal required properties
        preMove.id = ""; // Empty ID indicates this is a pre-move
        preMove.number = 0;
        preMove.color = fen.includes(" b ") ? "b" : "w";
        preMove.san = "";
        preMove.from = "";
        preMove.to = "";
        preMove.promotion = undefined;
        preMove.fen = fen;
        preMove.pgn = "";
        preMove.variationDepth = 0;
        preMove.parentMove = null;
        preMove.childMoves = undefined;
        preMove.previousMoveInline = null;
        preMove.nextMove = null;
        preMove.comment = undefined;
        preMove.nag = undefined;
        preMove.shapes = undefined;
        preMove.mcq = undefined;
        preMove.puzzleAnnotation = undefined;
        preMove.orient = undefined;
        preMove._preMove = null;
        preMove._userScore = null;
        preMove._isRevealed = false;
        preMove._gameResult = null;
        return preMove;
    }
    constructor(san, chess, previousMove, parentMove, initialFen = CHESS_START_POSITION) {
        // validate parentMove
        if (previousMove && previousMove.nextMove) {
            if (!parentMove) {
                parentMove = previousMove.nextMove;
            }
            else {
                if (parentMove.id !== previousMove.nextMove.id) {
                    console.log({ previousMove, parentMove });
                    throw new Error(`${san} Invalid parent move ${parentMove.id}, should be ${previousMove.nextMove.id}`);
                }
            }
        }
        // Only load if the chess instance is not already at the correct position
        // This preserves move history when moves are made sequentially (normal case in parseMoves)
        const prevMoveFen = previousMove?.fen ?? parentMove?.previousMove?.fen ?? null;
        const currentFen = chess.fen();
        if (prevMoveFen && currentFen !== prevMoveFen) {
            // Chess instance is not at the correct position, need to load
            chess.load(prevMoveFen);
        }
        else if (!prevMoveFen && currentFen !== initialFen) {
            // Starting position but chess is not there
            chess.load(initialFen);
        }
        // else: chess is already at the correct position, don't load (preserves move history)
        const prevFen = chess.fen();
        // Get move details from chess.js
        const moveResult = chess.move(san, { sloppy: true });
        if (!moveResult) {
            console.error(`Invalid move: ${san} from ${chess.pgn()}`);
            return;
        }
        const gameResult = checkGameResult(chess);
        if (gameResult) {
            this._gameResult = gameResult;
        }
        // Check for duplicate moves
        if (parentMove) {
            // Check if same as parent move
            if (parentMove.san === moveResult.san) {
                throw new Error(`Duplicate move: ${san} is the same as parent move`);
            }
            // Check siblings (other variations of the same parent)
            for (const sibling of parentMove.childMoves || []) {
                if (sibling.san === moveResult.san) {
                    throw new Error(`Duplicate move: ${san} already exists as a variation`);
                }
            }
        }
        // Basic properties setup
        this.number = parseInt(prevFen.split(" ")[5]) || 1;
        this.color = moveResult.color;
        this.san = moveResult.san;
        this.from = moveResult.from;
        this.to = moveResult.to;
        this.promotion = moveResult.promotion;
        this.fen = chess.fen();
        this.pgn = chess.pgn();
        this.variationDepth = 0;
        // Link previous move
        if (previousMove && !parentMove) {
            this.number =
                this.color === "w" ? previousMove.number + 1 : previousMove.number;
            this.variationDepth = previousMove.variationDepth;
            this.previousMoveInline = previousMove;
            previousMove.nextMove = this;
        }
        if (parentMove) {
            this.number = parentMove.number;
            this.variationDepth = parentMove.variationDepth + 1;
            this.parentMove = parentMove;
            if (parentMove.childMoves === undefined) {
                parentMove.childMoves = [];
            }
            parentMove.childMoves.push(this);
        }
        // Generate ID using the specified format
        const cleanSan = this.san.replace(/[#+]/g, "");
        const prevFenHash = this.hashFen(prevFen);
        this.id = `${this.number}-${this.variationDepth}-${this.color}-${cleanSan}-${prevFenHash}`;
    }
    hashFen(fen) {
        let hash = 0;
        for (let i = 0; i < fen.length; i++) {
            const char = fen.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash;
        }
        return Math.abs(hash).toString(16).slice(-4);
    }
    get previousMove() {
        return this.previousMoveInline || this.parentMove?.previousMove || null;
    }
    get isVariationBegin() {
        return this.variationDepth > 0 && !this.previousMoveInline;
    }
    get isVariationEnd() {
        return this.variationDepth > 0 && !this.nextMove;
    }
    get firstMoveInLine() {
        if (!this.previousMoveInline) {
            return this;
        }
        return this.previousMoveInline.firstMoveInLine;
    }
    get lastMoveInLine() {
        if (!this.nextMove) {
            return this;
        }
        return this.nextMove.lastMoveInLine;
    }
    /**
     * Returns a string representation of the move following PGN standards.
     * - NAGs are represented in their standard notation (!, ?, !!, etc.)
     * - Comments are enclosed in curly braces {}
     * - User move scores are included as a special comment {[%mus score]}
     * - Annotations (shapes) are included as ChessBase format {[%csl ...][%cal ...]}
     * - MCQ annotations are included as ChessBase format {[%mcq ...]}
     * @returns {string} PGN-compliant string representation of the move
     */
    toString(options = {}) {
        const { includeUserScore = true } = options;
        let result = this.san;
        // Add NAG if present
        if (this.nag) {
            result += ` ${this.nag}`;
        }
        // Add user score if enabled
        if (includeUserScore && this._userScore != null) {
            result += ` {[%mus ${this._userScore}]}`;
        }
        // Add annotations (shapes) if present
        if (this.shapes && this.shapes.length > 0) {
            const annotationString = chessgroundToChessBase(this.shapes);
            if (annotationString) {
                result += ` {${annotationString}}`;
            }
        }
        // Add orient annotation if present
        if (this.orient) {
            result += ` {${orientToChessBase(this.orient)}}`;
        }
        // Add MCQ annotation if present
        if (this.mcq) {
            const mcqString = mcqToChessBase(this.mcq);
            if (mcqString) {
                result += ` {${mcqString}}`;
            }
        }
        // Add comment if present
        if (this.comment) {
            result += ` {${this.comment}}`;
        }
        return result;
    }
    isInlineAndBefore(moveId) {
        let nextMove = this.nextMove;
        while (nextMove) {
            if (nextMove.id === moveId) {
                return true;
            }
            nextMove = nextMove.nextMove;
        }
        return false;
    }
    isInlineAndAfter(moveId) {
        let previousMove = this.previousMove;
        while (previousMove) {
            if (previousMove.id === moveId) {
                return true;
            }
            previousMove = previousMove.previousMove;
        }
        return false;
    }
    isInline(moveId) {
        return this.isInlineAndBefore(moveId) || this.isInlineAndAfter(moveId);
    }
    findMoveById(moveId) {
        if (this.id === moveId) {
            return this;
        }
        if (this.nextMove) {
            const foundMove = this.nextMove.findMoveById(moveId);
            if (foundMove) {
                return foundMove;
            }
        }
        for (const childMove of this.childMoves || []) {
            const foundMove = childMove.findMoveById(moveId);
            if (foundMove) {
                return foundMove;
            }
        }
        return null;
    }
    rows(options = {}) {
        const { includeVariations = true } = options;
        const self = this;
        let move = self;
        const moveRows = [];
        let currentMoveNumber = -1;
        while (move) {
            if (move.number !== currentMoveNumber ||
                move.variationDepth !== moveRows[moveRows.length - 1].variationDepth) {
                // new full move
                moveRows.push({
                    number: move.number,
                    variationDepth: move.variationDepth,
                    white: null,
                    black: null,
                });
                currentMoveNumber = move.number;
            }
            const currentRow = moveRows[moveRows.length - 1];
            if (move.color === "w") {
                if (currentRow.white) {
                    throw new Error(`Duplicate move number ${move.number}: ${currentRow.white.san} vs ${move.san}`);
                }
                currentRow.white = move;
            }
            else {
                if (currentRow.black) {
                    throw new Error(`Duplicate move number ${move.number}: ${currentRow.black.san} vs ${move.san}`);
                }
                currentRow.black = move;
            }
            if (includeVariations) {
                for (const childMove of move.childMoves || []) {
                    moveRows.push(...childMove.rows({ includeVariations }));
                }
            }
            move = move.nextMove;
        }
        return moveRows;
    }
    /**
     * The puzzle role of this move, derived purely from the chain:
     * - "answer":   player must make this board move
     * - "opponent": auto-played opponent move
     * - "masked":   hidden move after MCQ anchor (auto-reveals after MCQ is answered)
     * - null:       not a puzzle move (or the anchor move itself)
     *
     * MCQ is an anchor, not a role — use `this.mcq != null` to detect it.
     * Variation roots inherit the same role as their parentMove (alternative at same position).
     */
    get puzzleRole() {
        // Variation root: same role as parentMove (alternative at the same position)
        if (!this.previousMoveInline && this.parentMove) {
            return this.parentMove.puzzleRole;
        }
        // prev = inline predecessor, or _preMove back-link for root moves
        const prev = this.previousMoveInline ?? this._preMove ?? null;
        if (!prev)
            return null;
        // After a puzzle anchor (puzzleAnnotation) → player answers first
        if (prev.puzzleAnnotation != null)
            return "answer";
        // After MCQ anchor → masked chain starts
        if (prev.mcq != null)
            return "masked";
        // Pure alternation
        switch (prev.puzzleRole) {
            case "answer": return "opponent";
            case "opponent": return "answer";
            case "masked": return "masked"; // MCQ chain continues masked
            default: return null;
        }
    }
    /**
     * A move is a puzzle move if it has a non-null puzzleRole.
     * Derived from the chain via puzzleRole.
     */
    get isPuzzleMove() {
        return this.puzzleRole !== null;
    }
    /**
     * True when the move carries a negative NAG (?, ??, ?!) indicating the
     * coach marked it as incorrect. Used by puzzle logic to match the move
     * (so the coach's explanation is shown) but treat it as a wrong answer.
     */
    get isMarkedIncorrect() {
        if (this.nag == null)
            return false;
        return this.nag === "?" || this.nag === "??" || this.nag === "?!";
    }
    /**
     * Links this root move back to the preMove (puzzle anchor).
     * Only effective on root moves (no previousMoveInline, no parentMove).
     * Called by PgnDataBuilder after parseMoves() and by PgnData.addMove() for the first move.
     */
    setPreMove(prev) {
        if (!this.previousMoveInline && !this.parentMove) {
            this._preMove = prev;
        }
    }
    updateComment(comment) {
        this.comment = comment;
    }
    updateNag(nag) {
        this.nag = nag || undefined;
    }
    updateShapes(shapes) {
        this.shapes = shapes;
    }
    updateMcq(mcq) {
        this.mcq = mcq;
    }
    updatePuzzleAnnotation(annotation) {
        this.puzzleAnnotation = annotation;
    }
    updateOrient(orient) {
        this.orient = orient;
    }
    /**
     * True if this move requires the user to provide an answer
     * (either a board move or an MCQ selection).
     */
    get requiresAnswer() {
        return this.puzzleRole === "answer" || this.mcq != null;
    }
    /**
     * Determines if this question has been answered correctly.
     * Only meaningful if questionType is not null.
     * userScore = null: not answered
     * userScore = 0: revealed but not answered
     * userScore = -1: wrong answer
     * userScore = 1: correct answer
     *
     * @returns {boolean} True if the question has been answered correctly
     */
    get hasCorrectAnswer() {
        return this.requiresAnswer && this._userScore > 0;
    }
    /**
     * Returns a visual mask for hidden puzzle moves:
     * - '▯▯▯' for MCQ anchors that haven't been answered
     * - '???' for "answer" moves (player's turn) that haven't been answered
     * - '---' for "opponent" / "masked" moves in the puzzle sequence
     * - null for revealed moves (userScore !== null)
     *
     * @returns {string | null} The mask string or null if no mask
     */
    get mask() {
        // Don't mask if already answered/revealed
        if (this._userScore !== null || this._isRevealed)
            return null;
        // Move question: show ???
        if (this.puzzleRole === "answer")
            return "???";
        // MCQ anchor: show placeholder until answered
        if (this.mcq != null)
            return "▯▯▯";
        // Non-puzzle moves are never masked
        if (this.puzzleRole === null)
            return null;
        // Opponent move: auto-reveal once the preceding answer move has been played
        // (consistent with shouldReveal() — _isRevealed is runtime-only and lost on reconstruction)
        if (this.puzzleRole === "opponent" && this.previousMoveInline?._userScore !== null)
            return null;
        // Opponent / masked (post-MCQ chain): show ---
        return "---";
    }
    /**
     * Determines if this move should be revealed/shown to the user.
     *
     * @returns {boolean} True if the move should be revealed
     */
    shouldReveal() {
        // MCQ anchor: always revealed (user needs to see the position to answer)
        if (this.mcq != null)
            return true;
        // Non-puzzle moves are always revealed
        if (this.puzzleRole === null)
            return true;
        // Opponent moves auto-reveal once the preceding answer move has been played
        if (this.puzzleRole === "opponent" && this.previousMoveInline) {
            return this.previousMoveInline._userScore !== null || this._isRevealed;
        }
        // Answer/masked moves reveal when user played them or system revealed them
        return this._userScore !== null || this._isRevealed;
    }
    get displayColor() {
        if (this._userScore === -1)
            return "red";
        if (this.isPuzzleMove) {
            if (this._userScore === 1)
                return "green";
            if (this._userScore === 0)
                return "purple"; // user's intermediate move, always
        }
        if (this._userScore != null)
            return "purple"; // non-puzzle user move
        // No user score — auto-visible cases
        if (this.puzzleRole === "opponent")
            return "blue"; // derived-visible opponent
        if (this._isRevealed)
            return "blue"; // in-session solution reveal
        return null;
    }
    get isCheckmate() {
        return this.san.toString().includes("#");
    }
    get userScore() {
        return this._userScore;
    }
    /** True when this move was system-revealed (opponent auto-play or solution display). Runtime-only; not persisted in PGN. */
    get isRevealed() {
        return this._isRevealed;
    }
    /**
     * Get the game result associated with this move
     * @returns The game result or null if the game is not over
     */
    get gameResult() {
        return this._gameResult;
    }
    /**
     * Get a human-readable result string
     * @returns A human-readable result string or null if the game is not over
     */
    get resultString() {
        if (!this._gameResult) {
            return null;
        }
        // Determine the color of the last move
        const lastMoveColor = this.color === "w" ? CHESS_SIDE.WHITE : CHESS_SIDE.BLACK;
        return generateResultString(this._gameResult, lastMoveColor);
    }
    loadUserScore(score) {
        this._userScore = score;
    }
    findMatchedPuzzleMove(san) {
        // First check this move
        if (this.isPuzzleMove && this._userScore == null && this.san === san) {
            return this;
        }
        // Then check child moves (variations)
        const matchedMove = (this.childMoves || []).find((m) => m.isPuzzleMove && m._userScore == null && m.san === san);
        return matchedMove || null;
    }
    /**
     * Marks this move as system-revealed (opponent auto-play or solution display).
     * Does NOT set a user score — reveal state is runtime-only and not persisted in PGN.
     */
    recordRevealed() {
        if (this._isRevealed)
            return;
        if (!this.isPuzzleMove && this.mcq == null)
            return;
        this._isRevealed = true;
    }
    /**
     * Records the user's score for this move (guarded — no-op if already scored).
     * Scoring logic (1 = correct, -1 = wrong, 0 = intermediate) belongs in the caller
     * (ScenarioPuzzle.vue) which has full game context (mateIn, depth, checkmate detection).
     * @param score 1 = correct, -1 = wrong attempt, 0 = intermediate user move (default)
     */
    recordUserMove(score = 0) {
        if (this._userScore != null)
            return;
        this._userScore = score;
    }
}
